// frontend/src/pages/admin/ManageOIT.js
import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import Header from "../../components/Header";
import Sidebar from "../../components/Sidebar";
import Footer from "../../components/Footer";
import api from "../../services/api";
import Swal from "sweetalert2";

const ManageOIT = ({ user, setUser }) => {
  const [loading, setLoading] = useState(true);
  const [activeFiscalYear, setActiveFiscalYear] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [settings, setSettings] = useState(null);
  const [isSubmitPeriod, setIsSubmitPeriod] = useState(false);
  const [topics, setTopics] = useState([]);
  const [schoolInfo, setSchoolInfo] = useState(null);
  const [progress, setProgress] = useState(0);
  const [rejectedSubmissions, setRejectedSubmissions] = useState([]);
  const [showRejectedTab, setShowRejectedTab] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Get active fiscal year
        const fiscalYearRes = await api.get("/fiscal-years/active");
        setActiveFiscalYear(fiscalYearRes.data);

        // Get school info
        if (user && user.school_id) {
          const schoolRes = await api.get(`/schools/${user.school_id}`);
          setSchoolInfo(schoolRes.data);
        }

        if (fiscalYearRes.data) {
          // Get settings
          const settingsRes = await api.get(
            `/system-settings/${fiscalYearRes.data.id}`
          );
          setSettings(settingsRes.data);

          // Check if current date is within OIT submission period
          const now = new Date();
          const oitStartDate = new Date(settingsRes.data.oit_start_date);
          const oitEndDate = new Date(settingsRes.data.oit_end_date);
          setIsSubmitPeriod(now >= oitStartDate && now <= oitEndDate);

          // Get OIT topics with descriptions
          const topicsRes = await api.get(`/topics/4/${fiscalYearRes.data.id}`);
          setTopics(topicsRes.data);

          // Get OIT questions
          const questionsRes = await api.get(
            `/questionnaire/4/${fiscalYearRes.data.id}`
          );
          setQuestions(questionsRes.data);

          // Get OIT submissions
          const submissionsRes = await api.get(
            `/oit/${user.school_id}/${fiscalYearRes.data.id}`
          );
          setSubmissions(submissionsRes.data);
          
          // ดึงรายการที่ถูกตีกลับ
          const rejectedRes = await api.get(`/oit/rejected/${user.school_id}/${fiscalYearRes.data.id}`);
          setRejectedSubmissions(rejectedRes.data);
          
          if (rejectedRes.data.length > 0) {
              setShowRejectedTab(true);
          }

          // Calculate progress
          if (questionsRes.data.length > 0) {
            const totalSubmitted = submissionsRes.data.filter(s => s.link).length;
            const progressPercentage =
              (totalSubmitted / questionsRes.data.length) * 100;
            setProgress(progressPercentage);
          }
        }
      } catch (error) {
        console.error("Error fetching data:", error);
        Swal.fire({
          icon: "error",
          title: "การโหลดข้อมูลล้มเหลว",
          text: "ไม่สามารถโหลดข้อมูลได้ กรุณาลองใหม่ภายหลัง",
          confirmButtonText: "ตกลง",
        });
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [user.school_id]);

  // ฟังก์ชันแก้ไขข้อมูลโรงเรียน
  const handleEditSchoolInfo = async () => {
    try {
      const { value: formValues } = await Swal.fire({
        title: 'แก้ไขข้อมูลโรงเรียน',
        html: `
          <div class="row">
            <div class="col-12 mb-3">
              <label class="form-label">ชื่อโรงเรียน</label>
              <input id="school-name" class="form-control" value="${schoolInfo.name || ''}" required>
            </div>
            <div class="col-12 mb-3">
              <label class="form-label">ผู้อำนวยการ</label>
              <input id="school-director" class="form-control" value="${schoolInfo.director || ''}">
            </div>
            <div class="col-12 mb-3">
              <label class="form-label">ที่อยู่</label>
              <textarea id="school-address" class="form-control" rows="3">${schoolInfo.address || ''}</textarea>
            </div>
            <div class="col-12 mb-3">
              <label class="form-label">อีเมล</label>
              <input id="school-email" class="form-control" type="email" value="${schoolInfo.email || ''}">
            </div>
            <div class="col-12 mb-3">
              <label class="form-label">เบอร์โทรศัพท์</label>
              <input id="school-phone" class="form-control" value="${schoolInfo.phone || ''}">
            </div>
          </div>
        `,
        focusConfirm: false,
        showCancelButton: true,
        confirmButtonText: 'บันทึก',
        cancelButtonText: 'ยกเลิก',
        width: '600px',
        preConfirm: () => {
          const name = document.getElementById('school-name').value;
          const director = document.getElementById('school-director').value;
          const address = document.getElementById('school-address').value;
          const email = document.getElementById('school-email').value;
          const phone = document.getElementById('school-phone').value;

          if (!name) {
            Swal.showValidationMessage('กรุณากรอกชื่อโรงเรียน');
            return false;
          }

          return { name, director, address, email, phone };
        }
      });

      if (formValues) {
        await api.put(`/schools/${user.school_id}`, formValues);
        
        // อัปเดตข้อมูลใน state
        setSchoolInfo(prev => ({
          ...prev,
          ...formValues
        }));

        Swal.fire({
          icon: 'success',
          title: 'บันทึกข้อมูลสำเร็จ',
          text: 'อัปเดตข้อมูลโรงเรียนเรียบร้อยแล้ว',
          confirmButtonText: 'ตกลง'
        });
      }
    } catch (error) {
      console.error('Error updating school info:', error);
      Swal.fire({
        icon: 'error',
        title: 'การบันทึกข้อมูลล้มเหลว',
        text: error.response?.data?.message || 'ไม่สามารถบันทึกข้อมูลได้ กรุณาลองใหม่ภายหลัง',
        confirmButtonText: 'ตกลง'
      });
    }
  };

  const getSubmissionStatus = (questionId) => {
    const submission = submissions.find(
      (sub) => sub.question_id === questionId
    );
    return submission ? submission : null;
  };

  const getEvaluationStatus = (submission) => {
    if (
      !submission ||
      !submission.evaluations ||
      submission.evaluations.length === 0
    ) {
      return null;
    }

    let totalScore = 0;
    for (const evaluation of submission.evaluations) {
      totalScore += parseFloat(evaluation.score);
    }
    const avgScore = totalScore / submission.evaluations.length;

    return {
      avgScore,
      count: submission.evaluations.length,
      evaluations: submission.evaluations,
    };
  };

  // ฟังก์ชันจัดการหลายลิงก์
  const handleManageLinks = async (questionId) => {
    try {
      const submission = getSubmissionStatus(questionId);
      let existingLinks = [];
      
      if (submission && submission.link) {
        existingLinks = submission.link.split('\n').filter(link => link.trim());
      }

      const { value: result } = await Swal.fire({
        title: 'จัดการลิงก์ URL',
        html: `
          <div class="mb-3">
            <label class="form-label">ลิงก์ URL สำหรับตัวชี้วัดนี้:</label>
            <div id="links-container">
              ${existingLinks.map((link, index) => `
                <div class="input-group mb-2" data-index="${index}">
                  <input type="url" class="form-control link-input" value="${link}" placeholder="https://example.com">
                  <button class="btn btn-outline-danger remove-link" type="button">
                    <i class="fas fa-trash"></i>
                  </button>
                </div>
              `).join('')}
              ${existingLinks.length === 0 ? `
                <div class="input-group mb-2" data-index="0">
                  <input type="url" class="form-control link-input" placeholder="https://example.com">
                  <button class="btn btn-outline-danger remove-link" type="button">
                    <i class="fas fa-trash"></i>
                  </button>
                </div>
              ` : ''}
            </div>
            <button id="add-link" class="btn btn-outline-primary btn-sm">
              <i class="fas fa-plus me-1"></i> เพิ่มลิงก์
            </button>
          </div>
          <div class="alert alert-info">
            <small>
              <i class="fas fa-info-circle me-1"></i>
              สามารถเพิ่มหลายลิงก์ได้ หากมีข้อมูลในหลายหน้าเว็บ
            </small>
          </div>
        `,
        showCancelButton: true,
        confirmButtonText: 'บันทึก',
        cancelButtonText: 'ยกเลิก',
        width: '700px',
        didOpen: () => {
          let linkIndex = existingLinks.length || 1;

          document.getElementById('add-link').addEventListener('click', () => {
            const container = document.getElementById('links-container');
            const newLinkHtml = `
              <div class="input-group mb-2" data-index="${linkIndex}">
                <input type="url" class="form-control link-input" placeholder="https://example.com">
                <button class="btn btn-outline-danger remove-link" type="button">
                  <i class="fas fa-trash"></i>
                </button>
              </div>
            `;
            container.insertAdjacentHTML('beforeend', newLinkHtml);
            linkIndex++;
          });

          document.addEventListener('click', (e) => {
            if (e.target.closest('.remove-link')) {
              const linkContainer = e.target.closest('.input-group');
              const allContainers = document.querySelectorAll('#links-container .input-group');
              
              if (allContainers.length > 1) {
                linkContainer.remove();
              } else {
                linkContainer.querySelector('.link-input').value = '';
              }
            }
          });
        },
        preConfirm: () => {
          const linkInputs = document.querySelectorAll('.link-input');
          const links = Array.from(linkInputs)
            .map(input => input.value.trim())
            .filter(link => {
              if (link && !link.startsWith('http://') && !link.startsWith('https://')) {
                Swal.showValidationMessage(`ลิงก์ "${link}" ไม่ถูกต้อง ต้องขึ้นต้นด้วย http:// หรือ https://`);
                return false;
              }
              return !!link;
            });

          if (links.some(link => !link.startsWith('http://') && !link.startsWith('https://'))) {
            return false;
          }

          // Join even if empty to allow clearing links
          return links.join('\n');
        }
      });

      if (result !== undefined) {
        await api.post("/oit", {
          school_id: user.school_id,
          fiscal_year_id: activeFiscalYear.id,
          question_id: questionId,
          link: result,
        });

        Swal.fire({
          icon: "success",
          title: "บันทึกข้อมูลสำเร็จ",
          text: "บันทึกลิงก์ URL เรียบร้อยแล้ว",
          confirmButtonText: "ตกลง",
        });

        // Refresh data
        const submissionsRes = await api.get(`/oit/${user.school_id}/${activeFiscalYear.id}`);
        setSubmissions(submissionsRes.data);
        const rejectedRes = await api.get(`/oit/rejected/${user.school_id}/${activeFiscalYear.id}`);
        setRejectedSubmissions(rejectedRes.data);
        
        if (questions.length > 0) {
          const totalSubmitted = submissionsRes.data.filter(s => s.link).length;
          const progressPercentage = (totalSubmitted / questions.length) * 100;
          setProgress(progressPercentage);
        }
      }
    } catch (error) {
      console.error("Error submitting links:", error);
      Swal.fire({
        icon: "error",
        title: "การบันทึกข้อมูลล้มเหลว",
        text: error.response?.data?.message || "ไม่สามารถบันทึกลิงก์ URL ได้ กรุณาลองใหม่ภายหลัง",
        confirmButtonText: "ตกลง",
      });
    }
  };

  // ฟังก์ชันแสดงลิงก์ทั้งหมด
  const viewAllLinks = (submission) => {
    if (!submission || !submission.link) {
      Swal.fire({
        icon: 'info',
        title: 'ไม่มีลิงก์',
        text: 'ยังไม่มีการส่งลิงก์สำหรับตัวชี้วัดนี้',
        confirmButtonText: 'ตกลง'
      });
      return;
    }

    const links = submission.link.split('\n').filter(link => link.trim());
    
    let linksHtml = '<div class="list-group">';
    links.forEach((link, index) => {
      linksHtml += `
        <a href="${link}" target="_blank" rel="noopener noreferrer" class="list-group-item list-group-item-action">
          <div class="d-flex w-100 justify-content-between">
            <h6 class="mb-1">ลิงก์ที่ ${index + 1}</h6>
            <small><i class="fas fa-external-link-alt"></i></small>
          </div>
          <p class="mb-1 text-break">${link}</p>
        </a>
      `;
    });
    linksHtml += '</div>';

    Swal.fire({
      title: `ลิงก์ทั้งหมด (${links.length} ลิงก์)`,
      html: linksHtml,
      confirmButtonText: 'ปิด',
      width: '700px'
    });
  };

  const viewComments = (submission) => {
    if (
      !submission ||
      !submission.evaluations ||
      submission.evaluations.length === 0
    ) {
      Swal.fire({
        icon: "info",
        title: "ยังไม่มีการประเมิน",
        text: "ยังไม่มีกรรมการประเมินข้อมูลนี้",
        confirmButtonText: "ตกลง",
      });
      return;
    }

    let commentsList = "";
    submission.evaluations.forEach((evaluation, index) => {
      commentsList += `<div class="mb-3 p-3 border-bottom">
        <div><strong>กรรมการคนที่ ${index + 1}:</strong> ${
        evaluation.referee_name || "ไม่ระบุชื่อ"
      }</div>
        <div><strong>คะแนน:</strong> <span class="badge ${
          parseFloat(evaluation.score) >= 0.75
            ? "bg-success"
            : parseFloat(evaluation.score) >= 0.5
            ? "bg-warning"
            : "bg-danger"
        }">${parseFloat(evaluation.score) * 100}%</span></div>
        <div><strong>ความคิดเห็น:</strong> ${
          evaluation.comment || "(ไม่มีความคิดเห็น)"
        }</div>
      </div>`;
    });

    Swal.fire({
      title: "ผลการประเมินและความคิดเห็น",
      html: commentsList || "ไม่มีความคิดเห็น",
      confirmButtonText: "ปิด",
      width: "600px",
    });
  };

  const downloadSubmissions = () => {
    let csvContent = "ข้อ,ตัวชี้วัด,ลิงก์ URL,สถานะการประเมิน,คะแนน\n";
    const groupedQuestions = {};
    questions.forEach((q) => {
      if (!groupedQuestions[q.topic_name]) {
        groupedQuestions[q.topic_name] = [];
      }
      groupedQuestions[q.topic_name].push(q);
    });

    Object.keys(groupedQuestions).forEach((topicName) => {
      csvContent += `"${topicName.replace(/"/g, '""')}"\n`;

      groupedQuestions[topicName].forEach((question) => {
        const submission = getSubmissionStatus(question.id);
        const evaluation = getEvaluationStatus(submission);

        csvContent += `"${question.id}","${question.question_text.replace(/"/g, '""')}","${
          submission ? (submission.link || '').replace(/\n/g, ' | ') : ""
        }","${submission ? "ส่งแล้ว" : "ยังไม่ส่ง"}","${
          evaluation
            ? (evaluation.avgScore * 100).toFixed() + "%"
            : "ยังไม่ได้รับการประเมิน"
        }"\n`;
      });
    });

    const blob = new Blob(["\uFEFF" + csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute(
      "download",
      `OIT_${schoolInfo?.name || "โรงเรียน"}_${
        activeFiscalYear?.year || ""
      }.csv`
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  
    // ฟังก์ชันแสดงรายการที่ถูกตีกลับ
  const viewRejectedItems = () => {
    if (rejectedSubmissions.length === 0) {
        Swal.fire({
            icon: 'info',
            title: 'ไม่มีรายการที่ถูกตีกลับ',
            text: 'ไม่มี OIT ที่ถูกกรรมการตีกลับในขณะนี้',
            confirmButtonText: 'ตกลง'
        });
        return;
    }

    let rejectedHtml = '<div class="list-group text-start">';
    rejectedSubmissions.forEach((item, index) => {
        rejectedHtml += `
            <div class="list-group-item">
                <div class="d-flex w-100 justify-content-between">
                    <h6 class="mb-1 text-danger">ข้อ ${item.question_text}</h6>
                    <small class="text-muted">${new Date(item.rejected_at).toLocaleString('th-TH')}</small>
                </div>
                <p class="mb-1"><strong>เหตุผลการตีกลับ:</strong> ${item.rejection_reason}</p>
                <small class="text-muted">ตีกลับโดย: ${item.rejected_by_name}</small>
            </div>
        `;
    });
    rejectedHtml += '</div>';

    Swal.fire({
        title: `รายการที่ถูกตีกลับ (${rejectedSubmissions.length} รายการ)`,
        html: rejectedHtml,
        confirmButtonText: 'ปิด',
        width: '700px'
    });
  };


  if (loading) {
    return (
      <div className="wrapper">
        <Header user={user} title="จัดการข้อมูล OIT" />
        <Sidebar user={user} />
        <div className="content-wrapper">
          <div className="content-header">
            <div className="container-fluid">
              <div className="row mb-2">
                <div className="col-sm-6">
                  <h1 className="m-0">จัดการข้อมูล OIT</h1>
                </div>
              </div>
            </div>
          </div>
          <section className="content">
            <div className="container-fluid">
              <div
                className="d-flex justify-content-center align-items-center"
                style={{ height: "300px" }}
              >
                <div>
                  <div
                    className="spinner-border text-primary mb-3"
                    role="status"
                    style={{ width: "3rem", height: "3rem" }}
                  >
                    <span className="visually-hidden">กำลังโหลด...</span>
                  </div>
                  <div className="text-center">
                    กำลังโหลดข้อมูล กรุณารอสักครู่...
                  </div>
                </div>
              </div>
            </div>
          </section>
        </div>
        <Footer />
      </div>
    );
  }

  if (!activeFiscalYear) {
    return (
      <div className="wrapper">
        <Header user={user} title="จัดการข้อมูล OIT" />
        <Sidebar user={user} />
        <div className="content-wrapper">
          <div className="content-header">
            <div className="container-fluid">
              <div className="row mb-2">
                <div className="col-sm-6">
                  <h1 className="m-0">จัดการข้อมูล OIT</h1>
                </div>
              </div>
            </div>
          </div>
          <section className="content">
            <div className="container-fluid">
              <div className="alert alert-warning">
                <h5>
                  <i className="icon fas fa-exclamation-triangle"></i>{" "}
                  ยังไม่ได้กำหนดปีงบประมาณ
                </h5>
                <p>
                  ยังไม่มีปีงบประมาณที่กำลังใช้งานในขณะนี้
                  กรุณาติดต่อผู้ดูแลระบบเพื่อกำหนดปีงบประมาณ
                </p>
              </div>
            </div>
          </section>
        </div>
        <Footer />
      </div>
    );
  }

  const groupedQuestions = {};
  questions.forEach((q) => {
    if (!groupedQuestions[q.topic_name]) {
      groupedQuestions[q.topic_name] = [];
    }
    groupedQuestions[q.topic_name].push(q);
  });

  let globalIndex = 1;

  return (
    <div className="wrapper">
      <Header user={user} title="จัดการข้อมูล OIT" />
      <Sidebar user={user} />
      <div className="content-wrapper">
        <div className="content-header">
          <div className="container-fluid">
            <div className="row mb-2">
              <div className="col-sm-6">
                <h1 className="m-0">จัดการข้อมูล OIT</h1>
              </div>
              <div className="col-sm-6">
                <ol className="breadcrumb float-sm-end">
                  <li className="breadcrumb-item">
                    <Link to="/admin/dashboard">หน้าหลัก</Link>
                  </li>
                  <li className="breadcrumb-item active">จัดการข้อมูล OIT</li>
                </ol>
              </div>
            </div>
          </div>
        </div>

        <section className="content">
          <div className="container-fluid">
            {/* แสดงการ์ดแจ้งเตือนรายการที่ถูกตีกลับ */}
            {rejectedSubmissions.length > 0 && (
                <div className="alert alert-danger mb-4">
                    <div className="d-flex justify-content-between align-items-center">
                        <div>
                            <h5>
                                <i className="fas fa-exclamation-triangle me-2"></i>
                                มี OIT ที่ถูกตีกลับ ({rejectedSubmissions.length} รายการ)
                            </h5>
                            <p className="mb-0">กรุณาตรวจสอบและแก้ไข OIT ที่ถูกกรรมการตีกลับ</p>
                        </div>
                        <button 
                            className="btn btn-danger btn-sm"
                            onClick={viewRejectedItems}
                        >
                            <i className="fas fa-eye me-1"></i> ดูรายการ
                        </button>
                    </div>
                </div>
            )}
            
            <div className="row">
              <div className="col-lg-6">
                <div className="card">
                  <div className="card-header bg-primary">
                    <h3 className="card-title">
                      <i className="fas fa-school me-2"></i> ข้อมูลโรงเรียน
                    </h3>
                    <div className="card-tools">
                      <button 
                        className="btn btn-light btn-sm"
                        onClick={handleEditSchoolInfo}
                        title="แก้ไขข้อมูลโรงเรียน"
                      >
                        <i className="fas fa-edit me-1"></i> แก้ไข
                      </button>
                    </div>
                  </div>
                  <div className="card-body">
                    {schoolInfo && (
                      <div>
                        <h4>{schoolInfo.name}</h4>
                        <p className="mb-1">
                          <strong>รหัสโรงเรียน:</strong> {schoolInfo.code}
                        </p>
                        <p className="mb-1">
                          <strong>ผู้อำนวยการ:</strong>{" "}
                          {schoolInfo.director || "ไม่ระบุ"}
                        </p>
                        <p className="mb-1">
                          <strong>ที่อยู่:</strong>{" "}
                          {schoolInfo.address || "ไม่ระบุ"}
                        </p>
                        <p className="mb-1">
                          <strong>อีเมล:</strong>{" "}
                          {schoolInfo.email || "ไม่ระบุ"}
                        </p>
                        <p className="mb-1">
                          <strong>โทรศัพท์:</strong>{" "}
                          {schoolInfo.phone || "ไม่ระบุ"}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="col-lg-6">
                <div className="card">
                  <div className="card-header bg-info">
                    <h3 className="card-title">
                      <i className="fas fa-chart-pie me-2"></i>{" "}
                      ความคืบหน้าการกรอกข้อมูล OIT
                    </h3>
                  </div>
                  <div className="card-body">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <h4 className="mb-0">ความคืบหน้า</h4>
                      <span className="badge bg-primary">
                        {Math.round(progress)}%
                      </span>
                    </div>
                    <div className="progress mb-3" style={{ height: "25px" }}>
                      <div
                        className={`progress-bar progress-bar-striped progress-bar-animated ${
                          progress >= 80
                            ? "bg-success"
                            : progress >= 40
                            ? "bg-info"
                            : "bg-warning"
                        }`}
                        role="progressbar"
                        style={{ width: `${progress}%` }}
                      >
                        {Math.round(progress)}%
                      </div>
                    </div>
                    <div className="d-flex justify-content-between">
                      <div>
                        <i className="fas fa-check-circle text-success me-1"></i>{" "}
                        ส่งแล้ว: {submissions.filter(s => s.link).length} ข้อ
                      </div>
                      <div>
                        <i className="fas fa-times-circle text-danger me-1"></i>{" "}
                        ยังไม่ส่ง: {questions.length - submissions.filter(s => s.link).length} ข้อ
                      </div>
                      <div>
                        <i className="fas fa-list-ol text-primary me-1"></i>{" "}
                        ทั้งหมด: {questions.length} ข้อ
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="card mb-4">
              <div className="card-header bg-navy">
                <h3 className="card-title">
                  <i className="fas fa-info-circle me-2"></i> ข้อมูลการส่ง OIT
                  ปีงบประมาณ {activeFiscalYear.year}
                </h3>
                <div className="card-tools">
                  <button
                    type="button"
                    className="btn btn-light btn-sm"
                    onClick={downloadSubmissions}
                  >
                    <i className="fas fa-file-download me-1"></i>{" "}
                    ดาวน์โหลดข้อมูล
                  </button>
                </div>
              </div>
              <div className="card-body">
                {settings && (
                  <div className="alert alert-info mb-4">
                    <div className="row">
                      <div className="col-md-6">
                        <h5 className="alert-heading">
                          <i className="icon fas fa-calendar-alt me-2"></i>{" "}
                          ช่วงเวลาส่งข้อมูล OIT
                        </h5>
                        <p className="mb-0">
                          <strong>เริ่มต้น:</strong>{" "}
                          {new Date(settings.oit_start_date).toLocaleString(
                            "th-TH"
                          )}
                        </p>
                        <p>
                          <strong>สิ้นสุด:</strong>{" "}
                          {new Date(settings.oit_end_date).toLocaleString(
                            "th-TH"
                          )}
                        </p>
                      </div>
                      <div className="col-md-6">
                        <h5 className="alert-heading">
                          <i className="icon fas fa-info-circle me-2"></i>{" "}
                          สถานะปัจจุบัน
                        </h5>
                        <div
                          className={`alert ${
                            isSubmitPeriod ? "alert-success" : "alert-danger"
                          } mb-0`}
                        >
                          {isSubmitPeriod ? (
                            <>
                              <i className="fas fa-check-circle me-2"></i>{" "}
                              อยู่ในช่วงเวลาส่งข้อมูล (สามารถส่งข้อมูลได้)
                            </>
                          ) : (
                            <>
                              <i className="fas fa-times-circle me-2"></i>{" "}
                              อยู่นอกช่วงเวลาส่งข้อมูล (ไม่สามารถส่งข้อมูลได้)
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                
                <div className="alert alert-warning">
                  <h5>
                    <i className="icon fas fa-exclamation-triangle me-2"></i>{" "}
                    คำแนะนำในการกรอกข้อมูล OIT
                  </h5>
                  <ul className="mb-0">
                    <li>
                      ลิงก์ URL ที่แนบต้องเป็นลิงก์ที่เข้าถึงได้จริงและเป็นข้อมูลที่เผยแพร่ต่อสาธารณะ
                    </li>
                    <li>
                      สามารถแนบหลายลิงก์สำหรับตัวชี้วัดเดียวกันได้ หากมีข้อมูลอยู่ในหลายหน้าเว็บ
                    </li>
                    <li>
                      ข้อมูลที่นำมาเผยแพร่ต้องเป็นข้อมูลปัจจุบันและตรงกับตัวชี้วัดที่กำหนด
                    </li>
                    <li>
                      ควรตรวจสอบลิงก์ว่าสามารถเข้าถึงได้จากภายนอกหน่วยงานหรือไม่
                    </li>
                  </ul>
                </div>

                {Object.keys(groupedQuestions).length > 0 ? (
                  Object.keys(groupedQuestions).map((topicName, topicIndex) => (
                    <div key={topicIndex} className="mb-4">
                      <div className="card">
                        <div className="card-header bg-secondary">
                          <h4 className="card-title mb-0">
                            <i className="fas fa-folder me-2"></i> {topicName}
                          </h4>
                        </div>
                        <div className="table-responsive">
                          <table className="table table-bordered table-striped mb-0">
                            <thead className="table-dark">
                              <tr>
                                <th
                                  style={{ width: "5%" }}
                                  className="text-center"
                                >
                                  ข้อ
                                </th>
                                <th style={{ width: "40%" }}>ตัวชี้วัด</th>
                                <th
                                  style={{ width: "15%" }}
                                  className="text-center"
                                >
                                  สถานะ
                                </th>
                                <th
                                  style={{ width: "15%" }}
                                  className="text-center"
                                >
                                  คะแนนประเมิน
                                </th>
                                <th
                                  style={{ width: "25%" }}
                                  className="text-center"
                                >
                                  การจัดการ
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                            {groupedQuestions[topicName].map((question, index) => {
                                const submission = getSubmissionStatus(question.id);
                                const evaluation = getEvaluationStatus(submission);
                                const currentIndex = globalIndex++;
                                const linkCount = submission && submission.link ? 
                                    submission.link.split('\n').filter(link => link.trim()).length : 0;
                                const isSubmitted = submission && linkCount > 0;

                                return (
                                    <tr key={question.id} className={submission?.status === 'rejected' ? 'table-warning' : ''}>
                                        <td className="text-center align-middle">{currentIndex}</td>
                                        <td className="align-middle">
                                            <div>
                                                <strong>{question.question_text}</strong>
                                                <span className="badge bg-secondary ms-2">
                                                    น้ำหนัก {question.weight} คะแนน
                                                </span>
                                            </div>

                                            {/* แสดงเหตุผลการตีกลับ */}
                                            {submission?.status === 'rejected' && submission.rejection_reason && (
                                                <div className="mt-2">
                                                    <div className="alert alert-danger p-2 mb-0">
                                                        <small>
                                                            <strong>เหตุผลการตีกลับ:</strong> {submission.rejection_reason}
                                                        </small>
                                                    </div>
                                                </div>
                                            )}

                                            {/* แสดงจำนวนลิงก์ */}
                                            {isSubmitted && (
                                                <div className="mt-2">
                                                    <span className="badge bg-info">
                                                        <i className="fas fa-link me-1"></i>
                                                        {linkCount} ลิงก์
                                                    </span>
                                                </div>
                                            )}

                                            {/* คำอธิบายเพิ่มเติม */}
                                            {question.description && (
                                                <div className="mt-2">
                                                    <div className="alert alert-light border p-2 mb-0">
                                                        <div className="d-flex">
                                                            <div className="me-2">
                                                                <i className="fas fa-info-circle text-info mt-1"></i>
                                                            </div>
                                                            <div style={{ fontSize: "0.9rem" }}>
                                                                {question.description}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                            {question.explanation_link && (
                                                <div className="mt-2">
                                                    <a
                                                    href={question.explanation_link}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="btn btn-outline-info btn-sm"
                                                    >
                                                    <i className="fas fa-book-open me-1"></i>{" "}
                                                    ดูคู่มือ/คำอธิบายตัวชี้วัด
                                                    </a>
                                                </div>
                                            )}
                                        </td>
                                        <td className="text-center align-middle">
                                          {isSubmitted ? (
                                              <div>
                                                  {submission.status === 'pending' && (
                                                      <span className="badge bg-warning p-2">
                                                          <i className="fas fa-clock me-1"></i> รอตรวจสอบ
                                                      </span>
                                                  )}
                                                  {submission.status === 'approved' && (
                                                      <span className="badge bg-success p-2">
                                                          <i className="fas fa-check me-1"></i> อนุมัติแล้ว
                                                      </span>
                                                  )}
                                                  {submission.status === 'rejected' && (
                                                      <span className="badge bg-danger p-2">
                                                          <i className="fas fa-times me-1"></i> ตีกลับให้แก้ไข
                                                      </span>
                                                  )}
                                              </div>
                                          ) : (
                                              <span className="badge bg-secondary p-2">
                                                  <i className="fas fa-times me-1"></i> ยังไม่ส่ง
                                              </span>
                                          )}
                                        </td>
                                        <td className="text-center align-middle">
                                        {evaluation ? (
                                          <>
                                            <span
                                              className={`badge p-2 ${
                                                evaluation.avgScore >= 0.75
                                                  ? "bg-success"
                                                  : evaluation.avgScore >= 0.5
                                                  ? "bg-warning"
                                                  : "bg-danger"
                                              }`}
                                            >
                                              {(
                                                evaluation.avgScore * 100
                                              ).toFixed(0)}%
                                            </span>
                                            <div className="small text-muted mt-1">
                                              ({evaluation.count} การประเมิน)
                                            </div>
                                          </>
                                        ) : (
                                          <span className="badge bg-secondary p-2">
                                            <i className="fas fa-hourglass-half me-1"></i>{" "}
                                            รอการประเมิน
                                          </span>
                                        )}
                                      </td>
                                      <td className="text-center align-middle">
                                        <div className="btn-group-vertical btn-group-sm w-100">
                                          {isSubmitted && (
                                            <button
                                              className="btn btn-info btn-sm mb-1"
                                              onClick={() => viewAllLinks(submission)}
                                              title="ดูลิงก์ทั้งหมด"
                                            >
                                              <i className="fas fa-eye me-1"></i>
                                              ดูลิงก์ ({linkCount})
                                            </button>
                                          )}

                                          {isSubmitPeriod && (
                                            <button
                                              className="btn btn-primary btn-sm mb-1"
                                              onClick={() => handleManageLinks(question.id)}
                                              title="จัดการลิงก์"
                                              disabled={submission?.status === 'approved'}
                                            >
                                              <i className="fas fa-cogs me-1"></i>
                                              {isSubmitted ? "แก้ไข" : "เพิ่ม"}ลิงก์
                                            </button>
                                          )}

                                          {isSubmitted && (
                                            <button
                                              className="btn btn-secondary btn-sm"
                                              onClick={() => viewComments(submission)}
                                              title="ดูความคิดเห็น"
                                              disabled={!evaluation}
                                            >
                                              <i className="fas fa-comments me-1"></i>
                                              ความคิดเห็น
                                            </button>
                                          )}
                                        </div>
                                      </td>
                                    </tr>
                                );
                            })}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="alert alert-warning">
                    <h5>
                      <i className="icon fas fa-exclamation-triangle"></i>{" "}
                      ไม่พบแบบสอบถาม
                    </h5>
                    <p>
                      ยังไม่มีแบบสอบถาม OIT สำหรับปีงบประมาณนี้
                      กรุณาติดต่อผู้ดูแลระบบ
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>
      </div>
      <Footer />
    </div>
  );
};

export default ManageOIT;